/**
 * One switch: do employees see course and licence documents in the app?
 *
 * On by default (hidden). Saved the moment it is flipped, like the other switches that
 * take effect as a state; the office always keeps access to these documents.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Switch } from '@/components/Switch';
import { Card } from '@/components/ui';
import { usePermissions } from '@/features/access/usePermissions';
import { useI18n } from '@/i18n';
import { api, ApiError } from '@/lib/api';

export function CourseVisibilitySetting() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const readOnly = !usePermissions().can('settings', 'write');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (hide: boolean) => api.updateSettings({ hide_courses_from_employees: hide }),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  if (!settings.data) return null;

  // While a save is in flight show the value that was asked for, not the old one.
  const hidden = save.isPending && save.variables !== undefined
    ? save.variables
    : settings.data.settings.hideCoursesFromEmployees;

  return (
    <Card>
      <h2 className="font-bold">{t('settings.courses.title')}</h2>
      <div className="mt-3 flex items-start gap-3 rounded bg-surface-alt p-3">
        <span className="min-w-0 flex-1 text-sm">
          <strong>{t('settings.courses.hide')}</strong>
          <span className="mt-1 block text-xs leading-relaxed text-muted">{t('settings.courses.hideHint')}</span>
        </span>
        <Switch
          checked={hidden}
          onChange={(next) => save.mutate(next)}
          label={t('settings.courses.hide')}
          disabled={readOnly || save.isPending}
        />
      </div>
      {save.error ? (
        <p className="mt-3 text-sm text-danger" role="alert">
          {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
        </p>
      ) : null}
      {saved ? <p className="mt-3 text-sm font-semibold text-success">{t('settings.saved')}</p> : null}
    </Card>
  );
}
